import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { ChevronRight, ChevronLeft, MessageCircle, Map as MapIcon, Calendar, Sparkles, HelpCircle, Home, AlertOctagon, Stethoscope, ArrowRight } from "lucide-react";
import SeoHead from "@/components/SeoHead";
import { findArticle, relatedArticles, relatedByGroup, articleUrl, ARTICLE_GROUPS } from "@/lib/seoArticles";
import { useAllArticles } from "@/lib/useAllArticles";
import { renderText, cleanText, buildSlugIndex } from "@/lib/articleText";
import { AUTHOR_NAME, AUTHOR_URL, AUTHOR_PHOTO } from "@/pages/AuthorAshlee";
import { api } from "@/lib/api";
import { track, EVENTS } from "@/lib/analytics";
import { InArticleCta } from "@/components/InArticleCta";

/* SEO article template
 *
 * One component renders any registered article. Every standalone article
 * page in Memory Lane (/learn/:slug, /behavior/:slug, etc.) routes through
 * here, which keeps the SEO patterns (meta, JSON-LD, breadcrumbs, FAQ,
 * related, CTA) consistent across hundreds of URLs without copy-paste.
 *
 * Route adapter components (one per group) wrap this and pass the group
 * name so the slug lookup is unambiguous.
 */
export default function ArticlePage({ group }) {
    const { slug } = useParams();
    const navigate = useNavigate();
    const staticArticle = findArticle(group, slug);
    const [studioArticle, setStudioArticle] = React.useState(null);
    const [studioStatus, setStudioStatus] = React.useState(staticArticle ? "skip" : "loading");

    // Fall back to the studio API only when the slug isn't in the static
    // library. Studio articles come from /api/admin/content-studio/publish.
    React.useEffect(() => {
        if (staticArticle) {
            setStudioStatus("skip");
            return;
        }
        let cancelled = false;
        setStudioStatus("loading");
        api.get(`/articles/by-slug/${group}/${slug}`)
            .then((r) => {
                if (cancelled) return;
                // Slug-change redirect: backend returns {redirect: true, to: '/g/s'}
                // when an old slug is requested. Replace navigation so the
                // URL bar updates AND SEO bots see the canonical.
                if (r.data?.redirect && r.data?.to) {
                    navigate(r.data.to, { replace: true });
                    return;
                }
                setStudioArticle({ ...r.data, group, slug, source: "studio" });
                setStudioStatus("found");
                // Public view-count bump — fire-and-forget, idempotent enough.
                api.post(`/articles/by-slug/${group}/${slug}/view`).catch(() => { });
            })
            .catch(() => {
                if (cancelled) return;
                setStudioStatus("notfound");
            });
        return () => { cancelled = true; };
    }, [group, slug, staticArticle, navigate]);

    const article = staticArticle || studioArticle;

    // Build a fast slug→article lookup for [INTERNAL LINK: …] resolution.
    // Includes static + studio articles so cross-links to either work.
    const allArticles = useAllArticles();
    const slugIndex = React.useMemo(() => buildSlugIndex(allArticles), [allArticles]);

    // Article-view telemetry: one ping per mounted article (fires when
    // the slug changes, so SPA navigation between articles still counts).
    // GA4 sees the engagement; backend logs power the Founder Command
    // Center "top-trafficked article this week" opportunity card.
    // Hook must run before the early-return below — React requires hook
    // calls in the same order on every render — so we guard on `article`
    // inside the effect rather than around it.
    React.useEffect(() => {
        if (!article) return;
        track(EVENTS.RESOURCE_CLICK, {
            article_slug: article.slug,
            article_group: article.group,
            article_title: article.h1,
        });
        api.post("/admin/track/view", { slug: article.slug, group: article.group }).catch(() => { });
    }, [article]);

    if (!article) {
        // Still loading the studio API? Show a quiet spinner — almost
        // always resolves in < 200 ms, so we don't make a fuss.
        if (studioStatus === "loading") {
            return <div className="min-h-screen bg-cream" data-testid="article-loading" />;
        }
        return (
            <div className="min-h-screen bg-cream flex items-center">
                <SeoHead
                    title="Article not found — Memory Lane Care"
                    description="The page you're looking for doesn't exist. Browse Memory Lane's full library of caregiver guides."
                    canonicalPath="/"
                />
                <div className="max-w-xl mx-auto p-8 text-center">
                    <AlertOctagon className="mx-auto text-coral-dark mb-3" />
                    <h1 className="font-heading text-2xl text-[#12776F]">We can&apos;t find that article.</h1>
                    <p className="mt-2 text-[#4C5551]">It may have been renamed or it&apos;s on the way. Try the main library.</p>
                    <Link to="/" className="inline-block mt-5 ml-btn-primary">Back to Memory Lane</Link>
                </div>
            </div>
        );
    }

    const groupMeta = ARTICLE_GROUPS[article.group] || { label: article.group, href: "/" };
    const rels = relatedArticles(article);
    const crossGroup = relatedByGroup(article);
    const canonical = articleUrl(article);

    // JSON-LD: Article + FAQPage + BreadcrumbList in a single @graph.
    // Google honors FAQ rich results from FAQPage entries and breadcrumb
    // rich snippets from BreadcrumbList. Article powers the
    // "headline + description + dateModified" surface in search.
    const jsonLd = {
        "@context": "https://schema.org",
        "@graph": [
            {
                "@type": "Article",
                headline: cleanText(article.h1),
                description: cleanText(article.meta),
                datePublished: article.updated_at,
                dateModified: article.updated_at,
                // Person author — Ashlee Skabla Velez, APRN, ACNPC-AG.
                // Required for medical-content E-E-A-T. The `url` resolves to
                // a real ProfilePage at /authors/ashlee-skabla-velez so Google
                // can validate authorship.
                author: {
                    "@type": "Person",
                    name: AUTHOR_NAME,
                    url: AUTHOR_URL,
                    jobTitle: "Nurse Practitioner & Founder",
                    worksFor: { "@type": "Organization", name: "Memory Lane Care" },
                },
                publisher: {
                    "@type": "Organization",
                    name: "Memory Lane Care",
                    url: "https://memorylanecare.net/",
                    logo: {
                        "@type": "ImageObject",
                        url: "https://memorylanecare.net/logo192.png",
                    },
                },
                mainEntityOfPage: { "@type": "WebPage", "@id": canonical },
                articleSection: groupMeta.label,
            },
            {
                "@type": "BreadcrumbList",
                itemListElement: [
                    { "@type": "ListItem", position: 1, name: "Memory Lane", item: "https://memorylanecare.net/" },
                    { "@type": "ListItem", position: 2, name: groupMeta.label, item: `https://memorylanecare.net${groupMeta.href}` },
                    { "@type": "ListItem", position: 3, name: article.h1, item: `https://memorylanecare.net${canonical}` },
                ],
            },
            article.faq && article.faq.length > 0
                ? {
                    "@type": "FAQPage",
                    mainEntity: article.faq.map((f) => ({
                        "@type": "Question",
                        name: cleanText(f.q),
                        acceptedAnswer: { "@type": "Answer", text: cleanText(f.a) },
                    })),
                }
                : null,
        ].filter(Boolean),
    };

    const askLane = () => {
        // Pre-seeds Lane's prompt and lands the user in chat. /chat is
        // auth-gated; ProtectedRoute will route unauthed visitors through
        // /login first, then to /chat. The seed prompt sits in localStorage
        // across that redirect so Lane reads it when she finally mounts.
        if (article.lane_prompt) {
            localStorage.setItem("ml_lane_seed_prompt", article.lane_prompt);
        }
        navigate("/chat");
    };

    return (
        <div className="min-h-screen bg-cream">
            <SeoHead
                title={cleanText(article.title)}
                description={cleanText(article.meta)}
                canonicalPath={canonical}
                articleData={jsonLd}
            />

            {/* Breadcrumbs — also a JSON-LD candidate; we keep the visible
                trail simple here. Google reads it natively from anchors. */}
            <nav
                aria-label="Breadcrumb"
                data-testid="article-breadcrumbs"
                className="max-w-3xl mx-auto px-4 sm:px-6 pt-6 text-sm text-[#4C5551]"
            >
                <ol className="flex items-center gap-1.5 flex-wrap">
                    <li><Link to="/" className="hover:text-teal-dark inline-flex items-center gap-1"><Home size={12} strokeWidth={1.8} /> Memory Lane</Link></li>
                    <li><ChevronRight size={12} strokeWidth={1.8} className="text-[#6E7A74]" /></li>
                    <li><Link to={groupMeta.href} className="hover:text-teal-dark">{groupMeta.label}</Link></li>
                    <li><ChevronRight size={12} strokeWidth={1.8} className="text-[#6E7A74]" /></li>
                    <li className="text-[#1F2624] font-medium truncate max-w-[60vw]">{cleanText(article.h1)}</li>
                </ol>
            </nav>

            <article data-clarity-unmask="true" className="max-w-3xl mx-auto px-4 sm:px-6 py-6 md:py-10">
                {/* H1 + summary */}
                <header className="mb-6">
                    <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">{groupMeta.label}</p>
                    <h1 className="mt-1 text-3xl md:text-5xl font-heading font-semibold tracking-tight text-[#12776F] leading-[1.1]" data-testid="article-h1">
                        {cleanText(article.h1)}
                    </h1>
                    {article.summary && (
                        <p className="mt-4 text-lg md:text-xl text-[#4C5551] leading-relaxed">{renderText(article.summary, slugIndex, "summary")}</p>
                    )}
                    {article.updated_at && (
                        <p className="mt-3 text-xs text-[#6E7A74] inline-flex items-center gap-1">
                            <Calendar size={11} strokeWidth={1.8} /> Updated {article.updated_at}
                        </p>
                    )}
                </header>

                {/* Top author byline — a quiet single-row credit line.
                    Links to the full author page (Person + ProfilePage
                    schema validates this in Google's eyes). */}
                <AuthorByline />

                {/* Top CTAs — Roadmap + Ask Lane. Repeated at the bottom for
                    long-scrollers. */}
                <ArticleCtaBar onAskLane={askLane} testid="article-cta-top" />

                {/* Body — with a high-intent lead capture dropped at the
                    midpoint, where engaged readers actually are. */}
                <div className="prose prose-neutral max-w-none mt-8 space-y-5" data-testid="article-body">
                    {(() => {
                        const midIdx = Math.max(1, Math.floor(article.body.length / 2));
                        return article.body.map((block, i) => {
                        const cta = i === midIdx
                            ? <InArticleCta key={`cta-${i}`} slug={article.slug} group={article.group}
                                title={article.h1} lanePrompt={article.lane_prompt || ""} />
                            : null;
                        if (block.type === "h2") {
                            return <React.Fragment key={i}>{cta}<h2 className="font-heading text-2xl md:text-3xl text-[#12776F] mt-8 mb-2">{cleanText(block.text)}</h2></React.Fragment>;
                        }
                        if (block.type === "p") {
                            return <React.Fragment key={i}>{cta}<p className="text-[16px] text-[#1F2624] leading-relaxed">{renderText(block.text, slugIndex, `p-${i}`)}</p></React.Fragment>;
                        }
                        if (block.type === "ul") {
                            return (
                                <React.Fragment key={i}>{cta}
                                <ul className="list-disc pl-6 space-y-1.5 text-[16px] text-[#1F2624] leading-relaxed">
                                    {block.items.map((it, j) => <li key={j}>{renderText(it, slugIndex, `ul-${i}-${j}`)}</li>)}
                                </ul>
                                </React.Fragment>
                            );
                        }
                        if (block.type === "ol") {
                            return (
                                <React.Fragment key={i}>{cta}
                                <ol className="list-decimal pl-6 space-y-1.5 text-[16px] text-[#1F2624] leading-relaxed">
                                    {block.items.map((it, j) => <li key={j}>{renderText(it, slugIndex, `ol-${i}-${j}`)}</li>)}
                                </ol>
                                </React.Fragment>
                            );
                        }
                        if (block.type === "callout") {
                            const tone = block.tone === "coral"
                                ? "bg-coral-light/40 border-coral-light text-[#1F2624]"
                                : "bg-teal-soft/50 border-teal-light text-[#1F2624]";
                            return (
                                <React.Fragment key={i}>{cta}
                                <aside className={`rounded-2xl border px-4 py-3 my-4 italic ${tone}`}>
                                    {renderText(block.text, slugIndex, `callout-${i}`)}
                                </aside>
                                </React.Fragment>
                            );
                        }
                        return cta;
                        });
                    })()}
                </div>

                {/* FAQ — anchor-id'd so search engines surface them as snippets */}
                {article.faq?.length > 0 && (
                    <section id="faq" className="mt-12" data-testid="article-faq">
                        <h2 className="font-heading text-2xl md:text-3xl text-[#12776F] flex items-center gap-2">
                            <HelpCircle size={20} className="text-teal-dark" />
                            Frequently asked questions
                        </h2>
                        <dl className="mt-4 space-y-4">
                            {article.faq.map((f, i) => (
                                <div key={i} className="bg-white rounded-2xl border border-cream-dark p-4">
                                    <dt className="font-heading font-medium text-[#1F2624]">{cleanText(f.q)}</dt>
                                    <dd className="mt-1.5 text-[15px] text-[#4C5551] leading-relaxed">{renderText(f.a, slugIndex, `faq-${i}`)}</dd>
                                </div>
                            ))}
                        </dl>
                    </section>
                )}

                {/* Closing pitch — appears on every article, before the
                    bottom CTA + related links. Reinforces that the article
                    is one piece of a larger journey-companion product. */}
                <section className="mt-12 rounded-2xl bg-gradient-to-br from-teal-soft/40 via-cream-light to-coral-light/30 border border-teal-light p-5 md:p-6" data-testid="article-closing-pitch">
                    <p className="font-heading text-xl md:text-2xl text-[#12776F]">
                        Every dementia journey is different.
                    </p>
                    <p className="mt-2 text-[15px] text-[#4C5551] leading-relaxed">
                        Memory Lane Care helps you understand what applies to your loved one, what to expect next,
                        and which resources fit your family&apos;s situation.
                    </p>
                </section>

                {/* Full author bio box — appears at the foot of every
                    article. Combines the E-E-A-T trust signal with a
                    direct "book with Ashlee" path. */}
                <AuthorBox />

                {/* Bottom CTA repeat */}
                <ArticleCtaBar onAskLane={askLane} testid="article-cta-bottom" className="mt-4" />

                {/* Cross-group related — turns the corpus into a knowledge
                    graph. A Behavior article auto-surfaces matching
                    Medications + Benefits + Hard Conversations, etc. */}
                {Object.keys(crossGroup).length > 0 && (
                    <section className="mt-12" data-testid="article-knowledge-graph">
                        <h2 className="font-heading text-2xl text-[#12776F]">Related across the journey</h2>
                        <p className="text-sm text-[#4C5551] mt-1">
                            Memory Lane connects every part of dementia care. Here&apos;s how this topic threads into the rest.
                        </p>
                        <div className="mt-4 space-y-4">
                            {Object.entries(crossGroup).map(([groupKey, items]) => {
                                const gm = ARTICLE_GROUPS[groupKey] || { label: groupKey, href: "/" };
                                return (
                                    <div key={groupKey} data-testid={`article-graph-${groupKey}`}>
                                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark mb-2">
                                            {gm.label}
                                        </p>
                                        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                                            {items.map((r) => (
                                                <li key={r.slug}>
                                                    <Link
                                                        to={articleUrl(r)}
                                                        data-testid={`article-graph-link-${r.slug}`}
                                                        className="block bg-white border border-cream-dark rounded-2xl p-3 hover:border-teal-light transition-colors"
                                                    >
                                                        <p className="text-sm font-medium text-[#1F2624] leading-snug">{r.h1}</p>
                                                    </Link>
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                );
                            })}
                        </div>
                    </section>
                )}

                {/* Related articles — internal links for SEO + caregiver flow */}
                {rels.length > 0 && (
                    <section className="mt-12" data-testid="article-related">
                        <h2 className="font-heading text-2xl text-[#12776F]">Keep reading</h2>
                        <ul className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                            {rels.map((r) => (
                                <li key={r.slug}>
                                    <Link
                                        to={articleUrl(r)}
                                        data-testid={`article-related-${r.slug}`}
                                        className="block ml-card ml-card-hover p-4 hover:!border-teal-light"
                                    >
                                        <p className="text-xs uppercase tracking-wider font-semibold text-teal-dark">
                                            {(ARTICLE_GROUPS[r.group] || {}).label || r.group}
                                        </p>
                                        <p className="mt-0.5 font-heading text-lg text-[#1F2624] leading-snug">{r.h1}</p>
                                        <p className="mt-1 text-sm text-[#4C5551] line-clamp-2">{r.summary}</p>
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </section>
                )}

                {/* Back to group */}
                <div className="mt-12 pt-6 border-t border-cream-dark">
                    <Link to={groupMeta.href} className="inline-flex items-center gap-1.5 text-teal-dark hover:text-teal underline">
                        <ChevronLeft size={14} strokeWidth={1.8} /> Back to {groupMeta.label}
                    </Link>
                </div>
            </article>
        </div>
    );
}

function ArticleCtaBar({ onAskLane, testid, className = "" }) {
    return (
        <div
            data-testid={testid}
            className={`mt-6 pt-5 border-t border-cream-dark ${className}`}
        >
            <p className="text-sm font-medium text-[#4C5551]">Need help applying this to your situation?</p>
            <div className="mt-3 flex flex-col sm:flex-row sm:items-center gap-2.5">
                <Link
                    to="/?cta=roadmap"
                    data-testid={`${testid}-roadmap`}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-teal text-white font-medium text-sm px-6 py-2.5 hover:bg-teal-dark transition-colors w-full sm:w-auto"
                >
                    <MapIcon size={15} strokeWidth={1.8} /> Build my care roadmap
                </Link>
                <button
                    type="button"
                    onClick={onAskLane}
                    data-testid={`${testid}-ask-lane`}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-white border border-cream-dark text-sm text-[#1F2624] px-5 py-2.5 hover:border-teal-dark hover:text-teal-dark transition-colors w-full sm:w-auto"
                >
                    <MessageCircle size={15} strokeWidth={1.8} /> Ask Lane a question
                </button>
                <Link
                    to="/help-finder"
                    data-testid={`${testid}-find-local`}
                    onClick={() => track(EVENTS.FIND_LOCAL_RESOURCES, { source: "article_cta" })}
                    className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-2xl bg-white border border-cream-dark text-sm text-[#1F2624] px-5 py-2.5 hover:border-teal-dark hover:text-teal-dark transition-colors w-full sm:w-auto"
                >
                    <Sparkles size={15} strokeWidth={1.8} /> Explore local support
                </Link>
            </div>
        </div>
    );
}

/* Group adapter components — one per top-level group, registered in App.js */
export const LearnArticle = () => <ArticlePage group="learn" />;
export const BehaviorArticle = () => <ArticlePage group="behavior" />;
export const GuideArticle = () => <ArticlePage group="guide" />;
export const PayingForCareArticle = () => <ArticlePage group="paying-for-care" />;
export const ResourceArticle = () => <ArticlePage group="resources" />;
export const TreatmentArticle = () => <ArticlePage group="treatments" />;
export const HardConversationArticle = () => <ArticlePage group="hard-conversations" />;

/* ---------------- Author byline + box ---------------- */

/**
 * AuthorByline — quiet one-row credit just under the H1 + dateline.
 * Always renders so every article shows clinical authorship in the
 * first viewport. Links to the full author page.
 */
function AuthorByline() {
    return (
        <Link
            to="/authors/ashlee-skabla-velez"
            data-testid="article-author-byline"
            className="flex items-center gap-3 mt-4 group"
        >
            <img
                src={AUTHOR_PHOTO}
                alt={`Portrait of ${AUTHOR_NAME}`}
                className="w-10 h-10 rounded-full object-cover ring-2 ring-white shadow-sm shrink-0"
                loading="lazy"
            />
            <div className="leading-snug text-sm">
                <p className="text-[#1F2624]">
                    Written and clinically reviewed by{" "}
                    <span className="font-semibold group-hover:text-teal-dark transition-colors">
                        Ashlee Skabla Velez, APRN, ACNPC-AG
                    </span>
                </p>
                <p className="text-[13px] text-[#6E7A74]">Dementia care and caregiver-support specialist</p>
            </div>
        </Link>
    );
}

/**
 * AuthorBox — full-width card at the foot of every article. Bigger
 * trust signal than the byline + a direct "book with Ashlee" CTA so
 * readers who got value from the article can book a real conversation.
 */
function AuthorBox() {
    return (
        <aside
            className="mt-10 rounded-3xl bg-white border border-cream-dark p-5 md:p-6 flex flex-col sm:flex-row gap-4 sm:gap-5 items-start sm:items-center"
            data-testid="article-author-box"
            aria-label="About the author"
        >
            <Link to="/authors/ashlee-skabla-velez" className="shrink-0" data-testid="article-author-box-photo">
                <img
                    src={AUTHOR_PHOTO}
                    alt={`Portrait of ${AUTHOR_NAME}, founder of Memory Lane Care`}
                    className="w-20 h-20 sm:w-24 sm:h-24 rounded-2xl object-cover ring-2 ring-white shadow-md"
                    loading="lazy"
                />
            </Link>
            <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase tracking-wider font-semibold text-teal-dark">About the author</p>
                <Link
                    to="/authors/ashlee-skabla-velez"
                    data-testid="article-author-box-name"
                    className="font-heading text-xl text-[#1F2624] hover:text-teal-dark inline-block"
                >
                    Ashlee Skabla Velez, APRN, ACNPC-AG
                </Link>
                <p className="text-sm text-[#4C5551] mt-1 leading-relaxed">
                    Board-certified Acute Care Nurse Practitioner (Adult-Gerontology) with clinical
                    experience in <strong>hospice</strong>, <strong>dementia care</strong>, and the
                    <strong> CMS GUIDE Model</strong>. Founder of Memory Lane Care.
                </p>
                <div className="mt-3 flex flex-wrap gap-2">
                    <Link
                        to="/authors/ashlee-skabla-velez"
                        data-testid="article-author-box-profile"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-dark hover:underline"
                    >
                        <Stethoscope size={12} strokeWidth={2} /> View author profile
                    </Link>
                    <span aria-hidden="true" className="text-[#CBD5E0]">·</span>
                    <Link
                        to="/?book=1#landing-tabs"
                        data-testid="article-author-box-book"
                        className="inline-flex items-center gap-1 text-xs font-semibold text-teal-dark hover:underline"
                    >
                        Book a free 15-min walk-through <ArrowRight size={12} strokeWidth={2} />
                    </Link>
                </div>
            </div>
        </aside>
    );
}

